/**
 * MS SQL Server Credential Form
 *
 * A comprehensive form for configuring MS SQL Server credentials with:
 * - Connection: server, database, port, username, password
 * - Advanced: domain, timeouts, TDS version, encryption settings
 * Supports section-based rendering for tabbed display
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, Loader2, Database } from 'lucide-react';
import { credentialsApi } from '@/lib/api';
import { toast } from 'sonner';

export interface MsSqlFormData {
  // Basic Connection
  server: string;
  database: string;
  username: string;
  password: string;
  port: string;
  encrypt: boolean;

  // Advanced
  domain: string;
  connectTimeout: string;
  requestTimeout: string;
  trustServerCertificate: boolean;
  tdsVersion: string;
  enableArithAbort: boolean;
}

interface MsSqlCredentialFormProps {
  formData: MsSqlFormData;
  onChange: (data: Partial<MsSqlFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; details?: any } | null;
  testing?: boolean;
  onTest?: () => void;
  /** Which section to render - 'connection', 'advanced', or 'all' (default) */
  section?: 'connection' | 'advanced' | 'all';
}

export function MsSqlCredentialForm({
  formData,
  onChange,
  isEditing = false,
  section = 'all',
}: MsSqlCredentialFormProps) {
  const [fetchingDatabases, setFetchingDatabases] = useState(false);
  const [availableDatabases, setAvailableDatabases] = useState<string[]>([]);

  const handleChange = (field: keyof MsSqlFormData, value: any) => {
    onChange({ [field]: value });
  };

  const fetchDatabases = async () => {
    if (!formData.server || !formData.username) {
      toast.error('Please enter server and username first');
      return;
    }

    // Check if password is empty or masked (when editing existing credential)
    const maskedValue = '••••••••';
    if (!formData.password || formData.password === maskedValue) {
      toast.error('Please re-enter your password to fetch databases');
      return;
    }

    setFetchingDatabases(true);
    try {
      const response = await credentialsApi.listDatabases({
        type: 'msSqlCredential',
        data: {
          server: formData.server,
          port: formData.port || '1433',
          database: formData.database, // Used as fallback if user can't access master
          username: formData.username,
          password: formData.password,
          encrypt: formData.encrypt,
          trustServerCertificate: formData.trustServerCertificate,
        },
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        setAvailableDatabases(response.data);
        toast.success(`Found ${response.data.length} databases`);
      } else if (Array.isArray(response.data)) {
        toast.info('No databases found');
      } else {
        toast.error(response.data?.error || 'Failed to fetch databases');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'Failed to fetch databases');
    } finally {
      setFetchingDatabases(false);
    }
  };

  // Connection section
  const connectionContent = (
    <div className="space-y-4">
      {/* Server */}
      <div className="space-y-2">
        <Label htmlFor="server" className="text-sm font-medium">
          Server <span className="text-red-500">*</span>
        </Label>
        <Input
          id="server"
          placeholder="localhost or server.database.windows.net"
          value={formData.server}
          onChange={(e) => handleChange('server', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      {/* Port */}
      <div className="space-y-2">
        <Label htmlFor="port" className="text-sm font-medium">
          Port
        </Label>
        <Input
          id="port"
          placeholder="1433"
          value={formData.port ?? ''}
          onChange={(e) => handleChange('port', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      {/* Database with dropdown */}
      <div className="space-y-2">
        <Label htmlFor="database" className="text-sm font-medium">
          Database <span className="text-red-500">*</span>
        </Label>
        <div className="flex gap-2">
          {availableDatabases.length > 0 ? (
            <Select
              value={formData.database}
              onValueChange={(value) => handleChange('database', value)}
            >
              <SelectTrigger id="database" className="flex-1 font-mono text-sm">
                <SelectValue placeholder="Select database" />
              </SelectTrigger>
              <SelectContent>
                {availableDatabases.map((db) => (
                  <SelectItem key={db} value={db}>
                    <div className="flex items-center gap-2">
                      <Database className="h-3 w-3" />
                      {db}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="database"
              placeholder="MyDatabase"
              value={formData.database}
              onChange={(e) => handleChange('database', e.target.value)}
              className="flex-1 font-mono text-sm"
            />
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={fetchDatabases}
            disabled={fetchingDatabases || !formData.server}
            title="Fetch available databases"
          >
            {fetchingDatabases ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Enter server and credentials, then click refresh to list available databases
        </p>
      </div>

      {/* Username and Password */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="username" className="text-sm font-medium">
            Username <span className="text-red-500">*</span>
          </Label>
          <Input
            id="username"
            placeholder="sa or domain\\username"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            autoComplete="off"
            className="font-mono text-sm"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">
            Password <span className="text-red-500">*</span>
          </Label>
          <Input
            id="password"
            type="password"
            placeholder={isEditing ? "Leave unchanged or enter new" : "Enter password"}
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            autoComplete="new-password"
            className="font-mono text-sm"
          />
        </div>
      </div>
    </div>
  );

  // Advanced section
  const advancedContent = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="domain" className="text-sm font-medium">
          Windows Authentication Domain
        </Label>
        <Input
          id="domain"
          placeholder="MYDOMAIN"
          value={formData.domain}
          onChange={(e) => handleChange('domain', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="connectTimeout" className="text-sm font-medium">
            Connection Timeout (ms)
          </Label>
          <Input
            id="connectTimeout"
            type="number"
            placeholder="15000"
            value={formData.connectTimeout}
            onChange={(e) => handleChange('connectTimeout', e.target.value)}
            min="0"
            className="font-mono text-sm"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="requestTimeout" className="text-sm font-medium">
            Request Timeout (ms)
          </Label>
          <Input
            id="requestTimeout"
            type="number"
            placeholder="15000"
            value={formData.requestTimeout}
            onChange={(e) => handleChange('requestTimeout', e.target.value)}
            min="0"
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="tdsVersion" className="text-sm font-medium">
          TDS Protocol Version
        </Label>
        <Select
          value={formData.tdsVersion || 'auto'}
          onValueChange={(value) => handleChange('tdsVersion', value === 'auto' ? '' : value)}
        >
          <SelectTrigger id="tdsVersion">
            <SelectValue placeholder="Auto-detect" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto-detect (Recommended)</SelectItem>
            <SelectItem value="7_4">7.4 (SQL Server 2012+)</SelectItem>
            <SelectItem value="7_3_B">7.3 B (SQL Server 2008 R2)</SelectItem>
            <SelectItem value="7_3_A">7.3 A (SQL Server 2008)</SelectItem>
            <SelectItem value="7_2">7.2 (SQL Server 2005)</SelectItem>
            <SelectItem value="7_1">7.1 (SQL Server 2000)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="encrypt" className="text-sm font-medium">
            Encrypt Connection
          </Label>
          <p className="text-xs text-muted-foreground">
            Use TLS/SSL encryption
          </p>
        </div>
        <Switch
          id="encrypt"
          checked={formData.encrypt}
          onCheckedChange={(checked) => handleChange('encrypt', checked)}
        />
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="trustServerCertificate" className="text-sm font-medium">
            Trust Server Certificate
          </Label>
          <p className="text-xs text-muted-foreground">
            Trust self-signed certificates
          </p>
        </div>
        <Switch
          id="trustServerCertificate"
          checked={formData.trustServerCertificate}
          onCheckedChange={(checked) => handleChange('trustServerCertificate', checked)}
        />
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="enableArithAbort" className="text-sm font-medium">
            Enable ARITHABORT
          </Label>
          <p className="text-xs text-muted-foreground">
            Recommended for most scenarios
          </p>
        </div>
        <Switch
          id="enableArithAbort"
          checked={formData.enableArithAbort}
          onCheckedChange={(checked) => handleChange('enableArithAbort', checked)}
        />
      </div>
    </div>
  );

  // Return section-specific content or all
  if (section === 'connection') return connectionContent;
  if (section === 'advanced') return advancedContent;

  // Default: return all content (for backwards compatibility)
  return (
    <div className="space-y-6">
      {connectionContent}
      <div className="border-t pt-4">{advancedContent}</div>
    </div>
  );
}
